import React, { useCallback, useEffect, useRef, useState } from 'react';
import { LAYOUT } from '../layoutFit';
import { useSidePanelStore } from './side-panel-store';
import './shellPanel.css';

interface ShellPanelProps {
  /**
   * The panel is folded away, either because the shell found no room for it or
   * because the user closed it. Distinct from absent: the page still has an
   * occupant, so the model header's Ask AI button stays to bring it back.
   */
  asRail: boolean;
  /**
   * Told to the shell so it can hold the automatic fold while a gesture is in
   * flight.
   *
   * Without that, a drag crossing the width at which the panel stops fitting
   * folded it to a rail mid-gesture: the handle unmounted under the cursor and
   * the drag died. Clamping the width was tried first and was worse: the ceiling
   * lands at or below the current width on an ordinary laptop, so the panel
   * simply could not be widened at all.
   */
  onDragChange?: (dragging: boolean) => void;
  label?: string;
}

const KEYBOARD_STEP_PX = 16;

/**
 * The shell's panel column. Owns the frame, the drag handle and the rail, and
 * knows nothing about what docks inside it: the occupant portals itself into
 * `hostEl`. That split is the point. The chat rail lives on the model page and
 * needs the model's ids, so it stays owned there, while the width, the collapse
 * and the decision to fold belong to the shell, which is the only thing that
 * knows what else is on screen.
 */
export function ShellPanel({
  asRail,
  onDragChange,
  label = 'Panel',
}: ShellPanelProps) {
  const { width, setWidth, persistWidth, setHostEl } = useSidePanelStore();

  // Held in a ref, not state: the pointer handlers are bound once and a state
  // value would be captured at bind time and stay at the width the drag started
  // from, so every move would jump back to the origin.
  const dragging = useRef<{ startX: number; startWidth: number } | null>(null);
  // Only for the visual state. The drag itself never reads this, so a re-render
  // mid-drag cannot disturb it.
  const [isDragging, setIsDragging] = useState(false);

  const onPointerMove = useCallback(
    (event: PointerEvent) => {
      const drag = dragging.current;
      if (!drag) return;
      // The panel is on the right, so dragging left widens it.
      setWidth(drag.startWidth + (drag.startX - event.clientX));
    },
    [setWidth]
  );

  const endDrag = useCallback(() => {
    // Only persist if a drag was actually in flight, so an unrelated pointerup
    // anywhere on the page does not write.
    if (dragging.current) {
      persistWidth();
      onDragChange?.(false);
    }
    dragging.current = null;
    setIsDragging(false);
    document.body.style.removeProperty('cursor');
    document.body.style.removeProperty('user-select');
  }, [persistWidth, onDragChange]);

  useEffect(() => {
    window.addEventListener('pointermove', onPointerMove);
    window.addEventListener('pointerup', endDrag);
    // A cancelled pointer has to end the drag too. The browser fires this when
    // it decides the gesture was a scroll, and without handling it the drag
    // stayed armed with no moves arriving: the press registered and nothing
    // moved, which is what "not draggable" looked like.
    window.addEventListener('pointercancel', endDrag);
    return () => {
      window.removeEventListener('pointermove', onPointerMove);
      window.removeEventListener('pointerup', endDrag);
      window.removeEventListener('pointercancel', endDrag);
      // A drag interrupted by an unmount would otherwise leave the whole
      // document stuck with a resize cursor and no text selection.
      endDrag();
    };
  }, [onPointerMove, endDrag]);

  // Through the store rather than a local ref, because the occupant renders
  // elsewhere in the tree and portals in; it needs the element, not this
  // component. A callback ref so the store is told on mount and on unmount.
  const hostRef = useCallback(
    (el: HTMLDivElement | null) => setHostEl(el),
    [setHostEl]
  );

  const onPointerDown = (event: React.PointerEvent) => {
    dragging.current = { startX: event.clientX, startWidth: width };
    setIsDragging(true);
    onDragChange?.(true);
    // Capture, so every move lands here even once the pointer has left the 8px
    // strip, which it does immediately on any real drag. Window listeners mostly
    // cover this; capture also stops another element claiming the gesture.
    event.currentTarget.setPointerCapture?.(event.pointerId);
    // Set on the body rather than the handle: once the pointer leaves the strip
    // mid-drag the cursor would otherwise flicker back to the default.
    document.body.style.cursor = 'col-resize';
    document.body.style.userSelect = 'none';
    event.preventDefault();
  };

  const onKeyDown = (event: React.KeyboardEvent) => {
    if (event.key === 'ArrowLeft') {
      setWidth(width + KEYBOARD_STEP_PX);
      persistWidth();
      event.preventDefault();
    } else if (event.key === 'ArrowRight') {
      setWidth(width - KEYBOARD_STEP_PX);
      persistWidth();
      event.preventDefault();
    }
  };

  // Folded, nothing is drawn here. What reopens it is the Ask AI button in the
  // model header, drawn in both states, so there is no floating bubble sitting
  // over the page's own controls in the bottom-right corner.
  if (asRail) return null;

  return (
    <aside className="shpn" aria-label={label}>
      <div
        className="shpn-resize"
        role="separator"
        aria-orientation="vertical"
        aria-label={`Resize ${label}`}
        aria-valuenow={width}
        aria-valuemin={LAYOUT.CHAT_MIN_PX}
        aria-valuemax={LAYOUT.CHAT_MAX_PX}
        tabIndex={0}
        data-dragging={isDragging ? 'true' : undefined}
        onPointerDown={onPointerDown}
        onKeyDown={onKeyDown}
      />
      <div className="shpn-host" ref={hostRef} />
    </aside>
  );
}
